import React, { useState, useEffect } from 'react';
import { Card, Stack, Box, Button, Typography } from '@mui/material';
import {
  LISTING_COLUMNS,
  ROKU_LISTING_ACTIONS,
} from '../../config/module-configs/roku-packages.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import { getListData } from '../../services/channel-packager';
import { ACTIONS } from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';
import useAccess from '../../hooks/access.hook';
import AddChannelPackager from '../app-content/add-channel-packager.component';
import SnackbarInfo from '../common/snackbar-info.component';
import useFilteredActions from '../../hooks/filtered-action.hook';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListRokuPackages = () => {
  const columns = LISTING_COLUMNS;
  const actions = useFilteredActions('channel_packager', ROKU_LISTING_ACTIONS);

  const { getSignal, isAbortError } = useAbortableRequest();
  const { hasOperationAccess } = useAccess();
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [channelSummary, setChannelSummary] = useState('');

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    setSnackbarInfo({
      show: false,
      type: '',
      message: '',
    });
    setTimeout(() => {
      if (message) {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message,
        });
      }
    }, 200);

    handleActionCancel();
    handleRefreshData();
  };

  useEffect(() => {
    const signal = getSignal();

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setChannelSummary(res?.data?.packageUsedInChannels || '');
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            error: false,
          });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {hasOperationAccess('channel_packager', 'create') && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
            >
              Upload New Package
            </Button>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginTop: 3, color: 'red' }}
          >
            {`Please make sure to upload all ROKU channels with Splash Screens., navigate to Channel
              Settings > Action > Manage Web Content, then select the "Assets & Theme"
              tab.`}
          </Typography>
        </div>
        <div>
          <Box
            display="flex"
            gap={2}
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            {channelSummary && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexWrap: 'nowrap', width: '150px' }}
              >
                <Typography component="span" fontWeight="bold">
                  Summary: {channelSummary}
                </Typography>
              </Typography>
            )}
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="ic:twotone-refresh" />}
              sx={{ marginRight: 0 }}
              onClick={handleRefreshData}
              disabled={options.loading}
            >
              Refresh
            </Button>
          </Box>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      {(doAction.action === ACTIONS.CREATE.value ||
        doAction.action === ACTIONS.ASSIGN_CHANNELS.value) && (
        <AddChannelPackager
          title={
            doAction.action === ACTIONS.CREATE.value
              ? 'Upload Roku Package'
              : 'Assign package to channels'
          }
          packageId={doAction?.data?.id || 0}
          platform="Roku"
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          action={doAction.action}
          isGlobal={doAction?.data?.is_global || 0}
          releaseNotes={doAction?.data?.notes || ''}
          version={doAction?.data?.version || ''}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Page"
          message={`Do you want to delete ${doAction.data.title} page? You can not undo this action!`}
          onCancel={handleActionCancel}
          successButtonText="Yes"
          cancelButtonText="No"
          confirmationInputText="delete"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListRokuPackages.propTypes = {
  // value: PropTypes.string.isRequired,
};
export default ListRokuPackages;
